import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ClearIcon from "@mui/icons-material/ClearAll";
import TerminalIcon from "@mui/icons-material/Terminal";

// -----------------------------------------------------------------------------
// Props & helper types
// -----------------------------------------------------------------------------

export type OpsConsoleProps = {
  /** Base HTTP API URL, e.g., https://api.example.com/v1/admin/ops */
  httpBaseUrl: string;
  /** Base WS URL, e.g., wss://api.example.com/v1/admin/ops */
  wsBaseUrl: string;
  /** Function returning a JWT for the current user */
  getJwt: () => Promise<string> | string | null;
  /** Optional: initial command */
  initialLine?: string;
  /** Optional: whether to auto-focus the input on mount */
  autoFocus?: boolean;
};

// Server protocol messages
type WsStdout = { type: "stdout"; data: string };
type WsStderr = { type: "stderr"; data: string };
type WsExit = { type: "exit"; ok: boolean; elapsed_ms?: number };
type WsReady = { type: "ready" };

type Completion = {
  value: string;
  type: "namespace" | "method" | "param" | string;
  help?: string | null;
};

type CompleteResponse = {
  prefix: string;
  suggestions: Completion[];
};

// -----------------------------------------------------------------------------
// Utility
// -----------------------------------------------------------------------------

const joinUrl = (base: string, path: string) =>
  `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

const ANSI_RE = new RegExp(String.raw`(?:\u001B\[[0-9;]*[ -/]*[@-~])`, "g"); // generic ANSI CSI

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string)
  );

const ansiToHtml = (text: string): string => {
  // 1) strip ANSI sequences, 2) escape HTML so innerHTML is safe
  const stripped = text.replace(ANSI_RE, "");
  return escapeHtml(stripped);
};

const useLatest = <T,>(value: T) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

// Replace the token at the given cursor position with replacement
function replaceTokenAt(line: string, cursor: number, replacement: string) {
  // Identify token boundaries (whitespace-delimited)
  let start = cursor;
  let end = cursor;
  // If cursor is in the middle of text, adjust to select the current token
  if (start > line.length) start = line.length;
  if (end > line.length) end = line.length;
  // Move start left to previous whitespace
  while (start > 0 && !/\s/.test(line[start - 1])) start--;
  // Move end right to next whitespace
  while (end < line.length && !/\s/.test(line[end])) end++;
  const before = line.slice(0, start);
  const after = line.slice(end);
  // Ensure single space separation around tokens when needed
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const needsSpaceAfter = after.length > 0 && !/^\s/.test(after);
  const left = needsSpaceBefore ? before + " " : before;
  const right = needsSpaceAfter ? " " + after : after;
  const next = left + replacement + right;
  const newCursor = (left + replacement).length; // caret at end of inserted token
  return { next, newCursor };
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const OpsConsole: React.FC<OpsConsoleProps> = ({
  httpBaseUrl,
  wsBaseUrl,
  getJwt,
  initialLine = "ops ",
  autoFocus = true,
}) => {
  const [line, setLine] = useState(initialLine);
  const [running, setRunning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [log, setLog] = useState<string>("");
  const [statusChip, setStatusChip] = useState<{
    label: string;
    color: "default" | "success" | "warning" | "error";
  }>({ label: "Idle", color: "default" });
  const [suggestions, setSuggestions] = useState<Completion[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [selIndex, setSelIndex] = useState<number>(0);
  const [busyComplete, setBusyComplete] = useState(false);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const latestLog = useLatest(log);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Track cursor for smarter completion
  const [cursor, setCursor] = useState(0);

  // Auto-scroll output
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [log]);

  // ---------------------------------------------------------------------------
  // Exec flow (open WS, then send line)
  // ---------------------------------------------------------------------------

  const append = useCallback((chunk: string) => {
    setLog((prev) => (prev ? prev + chunk : chunk));
  }, []);

  const clearLog = useCallback(() => {
    setLog("");
    setElapsedMs(null);
  }, []);

  const stop = useCallback(() => {
    try {
      socket?.close();
    } catch (_) { }
  }, [socket]);

  const run = useCallback(async () => {
    if (!line.trim()) return;
    setConnecting(true);
    setRunning(true);
    setStatusChip({ label: "Connecting", color: "warning" });
    setElapsedMs(null);

    try {
      const token = await Promise.resolve(getJwt());
      if (!token) {
        setStatusChip({ label: "No JWT", color: "error" });
        setConnecting(false);
        setRunning(false);
        return;
      }
      const wsUrl = new URL(joinUrl(wsBaseUrl, "/exec"));
      wsUrl.searchParams.set("token", token); // WS auth via ?token=<JWT>

      const ws = new WebSocket(wsUrl.toString());
      setSocket(ws);

      ws.onopen = () => {
        setStatusChip({ label: "Running", color: "success" });
        ws.send(JSON.stringify({ line }));
      };

      ws.onmessage = (ev) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          append(ev.data + "\n");
          return;
        }
        if ((msg as WsReady).type === "ready") return; // optional
        if ((msg as WsStdout).type === "stdout") {
          append(msg.data);
        } else if ((msg as WsStderr).type === "stderr") {
          append(msg.data);
        } else if ((msg as WsExit).type === "exit") {
          setElapsedMs(msg.elapsed_ms ?? null);
          setStatusChip({ label: msg.ok ? "Done" : "Failed", color: msg.ok ? "success" : "error" });
          setRunning(false);
        }
      };

      ws.onerror = () => {
        setStatusChip({ label: "WS Error", color: "error" });
        setRunning(false);
      };

      ws.onclose = () => {
        setConnecting(false);
        setRunning(false);
      };
    } catch (err) {
      setStatusChip({ label: "Client Error", color: "error" });
      setConnecting(false);
      setRunning(false);
    }
  }, [getJwt, line, wsBaseUrl, append]);

  // ---------------------------------------------------------------------------
  // Autocomplete (HTTP POST /complete with Authorization: Bearer <JWT>)
  // ---------------------------------------------------------------------------

  const requestCompletions = useCallback(
    async (currentLine: string, caret: number) => {
      setBusyComplete(true);
      try {
        const token = await Promise.resolve(getJwt());
        const resp = await fetch(joinUrl(httpBaseUrl, "/complete"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ line: currentLine, cursor: caret, refresh_tree: false }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: CompleteResponse = await resp.json();
        setSuggestions(data.suggestions || []);
        setSelIndex(0);
        setShowSuggest(true);
      } catch (e) {
        setSuggestions([]);
        setShowSuggest(false);
      } finally {
        setBusyComplete(false);
      }
    },
    [getJwt, httpBaseUrl]
  );

  const applySuggestion = useCallback(
    (s: Completion | undefined) => {
      if (!s) return;
      const { next, newCursor } = replaceTokenAt(line, cursor, s.value);
      const needsTrailingSpace = s.type === "param" && !s.value.includes("=");
      const final = needsTrailingSpace ? next.replace(/\s*$/, " ") : next;
      setLine(final);
      setShowSuggest(false);
      // Restore caret after state update
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const pos = needsTrailingSpace ? newCursor + 1 : newCursor;
          inputRef.current.setSelectionRange(pos, pos);
          inputRef.current.focus();
          setCursor(pos);
        }
      });
    },
    [cursor, line]
  );

  // Keyboard handling: Enter to run, Tab for completion, Up/Down history
  const [history, setHistory] = useState<string[]>([]);
  const [histIndex, setHistIndex] = useState<number>(-1);

  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (showSuggest && suggestions.length > 0) {
          // Commit current suggestion instead of executing
          e.preventDefault();
          applySuggestion(suggestions[selIndex]);
          // Ensure a space after the committed token so you can keep typing
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el) return;
            const val = el.value;
            if (!/\s$/.test(val)) {
              const pos = el.selectionStart ?? val.length;
              const next = val + " ";
              setLine(next);
              requestAnimationFrame(() => {
                const p = pos + 1;
                el.setSelectionRange(p, p);
                setCursor(p);
              });
            }
          });
          return;
        }
        e.preventDefault();
        if (!running && !connecting) {
          setHistory((h) => [line, ...h].slice(0, 100));
          setHistIndex(-1);
          run();
        }
      } else if (e.key === " " /* space */) {
        if (showSuggest && suggestions.length > 0) {
          e.preventDefault();
          applySuggestion(suggestions[selIndex]);
          // Force a trailing space after commit
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el) return;
            const val = el.value;
            if (!/\s$/.test(val)) {
              const pos = el.selectionStart ?? val.length;
              const next = val + " ";
              setLine(next);
              requestAnimationFrame(() => {
                const p = pos + 1;
                el.setSelectionRange(p, p);
                setCursor(p);
              });
            }
          });
          return;
        }
      } else if (e.key === "Tab") {
        e.preventDefault();
        if (!showSuggest) {
          await requestCompletions(line, cursor);
        } else if (suggestions.length > 0) {
          // Cycle selection; Shift+Tab goes backward
          setSelIndex((i) => {
            const n = suggestions.length;
            const next = (i + (e.shiftKey ? -1 : 1) + n) % n;
            return next;
          });
        }
      } else if (e.key === "ArrowUp") {
        if (showSuggest && suggestions.length > 0) {
          e.preventDefault();
          setSelIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (history.length > 0) {
          e.preventDefault();
          const next = Math.min(histIndex + 1, history.length - 1);
          setHistIndex(next);
          setLine(history[next]);
          requestAnimationFrame(() => {
            if (inputRef.current) {
              const pos = history[next].length;
              inputRef.current.setSelectionRange(pos, pos);
              setCursor(pos);
            }
          });
        }
      } else if (e.key === "ArrowDown") {
        if (showSuggest && suggestions.length > 0) {
          e.preventDefault();
          setSelIndex((i) => Math.min(i + 1, Math.max(0, suggestions.length - 1)));
          return;
        }
        if (history.length > 0) {
          e.preventDefault();
          const next = Math.max(histIndex - 1, -1);
          setHistIndex(next);
          const value = next === -1 ? "ops " : history[next];
          setLine(value);
          requestAnimationFrame(() => {
            if (inputRef.current) {
              const pos = value.length;
              inputRef.current.setSelectionRange(pos, pos);
              setCursor(pos);
            }
          });
        }
      } else if (e.key === "Escape") {
        setShowSuggest(false);
      } else if (showSuggest && e.key === "PageDown") {
        e.preventDefault();
        setSelIndex((i) => Math.min(i + 1, Math.max(0, suggestions.length - 1)));
      } else if (showSuggest && e.key === "PageUp") {
        e.preventDefault();
        setSelIndex((i) => Math.max(i - 1, 0));
      }
    },
    [
      applySuggestion,
      connecting,
      cursor,
      histIndex,
      history,
      line,
      requestCompletions,
      run,
      running,
      selIndex,
      showSuggest,
      suggestions,
    ]
  );


  // Reposition suggestion popover: keep it simple below the input
  const SuggestList = useMemo(() => {
    if (!showSuggest || suggestions.length === 0) return null;
    return (
      <Paper elevation={3} sx={{ position: "absolute", top: "100%", left: 0, mt: 0.5, width: "100%", maxHeight: 220, overflowY: "auto", zIndex: 10 }}>
        {suggestions.map((s, idx) => (
          <Box key={idx} onMouseDown={(e) => e.preventDefault()} onClick={() => applySuggestion(s)} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, cursor: "pointer", bgcolor: idx === selIndex ? "action.hover" : "background.paper" }}>
            <Chip size="small" label={s.type} sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{s.value}</Typography>
            {s.help && (
              <Typography variant="caption" sx={{ ml: "auto", opacity: 0.7 }}>{s.help}</Typography>
            )}
          </Box>
        ))}
      </Paper>
    );
  }, [applySuggestion, selIndex, showSuggest, suggestions]);

  const copyLog = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(latestLog.current || "");
    } catch { }
  }, [latestLog]);

  // Keep cursor in sync on typing & clicks
  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLine(e.target.value);
    setShowSuggest(false);
    setCursor(e.target.selectionStart ?? e.target.value.length);
  }, []);

  const onSelect = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setCursor(target.selectionStart ?? 0);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TerminalIcon /> Ops Console
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip size="small" label={statusChip.label} color={statusChip.color} />
          {elapsedMs != null && (
            <Chip size="small" label={`${elapsedMs} ms`} />
          )}
        </Box>
      </Box>

      <Box sx={{ position: "relative" }}>
        <TextField
          inputRef={inputRef}
          autoFocus={autoFocus}
          fullWidth
          label="Command"
          placeholder="ops <namespace> <method> --param value ..."
          value={line}
          onChange={onChange}
          onSelect={onSelect}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Chip size="small" label={connecting ? "WS" : "CMD"} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Autocomplete (Tab)"><span>
                  <IconButton onClick={() => requestCompletions(line, cursor)} disabled={busyComplete}>
                    {busyComplete ? <CircularProgress size={18} /> : <Typography variant="button">TAB</Typography>}
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Run (Enter)"><span>
                  <IconButton color="primary" onClick={run} disabled={running || connecting}>
                    <PlayArrowIcon />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Stop"><span>
                  <IconButton color="warning" onClick={stop} disabled={!running && !connecting}>
                    <StopIcon />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Clear output"><span>
                  <IconButton onClick={clearLog}>
                    <ClearIcon />
                  </IconButton>
                </span></Tooltip>
              </InputAdornment>
            ),
          }}
        />
        {SuggestList}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, height: "55vh", display: "flex", flexDirection: "column" }}>
        <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {/* Render as sanitized text (ANSI stripped); swap with a parser if needed */}
          <div dangerouslySetInnerHTML={{ __html: ansiToHtml(log) }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
          <Tooltip title="Copy output"><span>
            <IconButton onClick={copyLog}>
              <ContentCopyIcon />
            </IconButton>
          </span></Tooltip>
        </Box>
      </Paper>

      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        • Tips: Press <b>Tab</b> for completions, <b>↑/↓</b> for history, <b>Enter</b> to run, <b>Esc</b> to dismiss suggestions,
        Rerun with <b>--yes</b> to confirm if prompted for confirmation.
      </Typography>
    </Box>
  );
};

export default OpsConsole;
